import type { PostInputMetaData } from '../../graphqlTypes'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import type { MapperContext } from '../../types'
import type { ServiceMapperEntry } from '../types'

// YouTube's channelData is populated with real values for every field from the
// moment a draft is created (localStorage-backed `youtube_defaults`, applied at
// `Draft` construction) and there's no reminder-fields allow-list for its
// `short` update type, so channelData always passes through unfiltered -- in
// practice there is no "partially filled" state to handle. Every leaf below is
// a raw passthrough: legacy already stores `license`/`privacyStatus` using the
// same string values the GraphQL enums expect, so no remap is needed.
//
// `title`/`categoryId` are required on create and reject explicit `null`/`''`
// on edit (the backend's edit mapper throws), so both are OMITTED rather than
// nulled when falsy -- on create this surfaces the backend's own
// "title and categoryId are required" error, and on edit omitting preserves
// the stored value exactly like every other optional field.
//
// The remaining leaves are no-ops on edit whether omitted or sent as an
// explicit `null` (the backend's edit mapper guards every one of them with
// `!== undefined && !== null`), so `clearable()` buys nothing here -- they're
// sent as their real value when present and omitted otherwise.
//
// `ctx.channelVariant` (when present) supplies the same `channelData`.
export function mapYoutube(
  draft: Draft,
  ctx: MapperContext,
): PostInputMetaData {
  const variant = ctx.channelVariant
  const data = variant
    ? variant.channelData?.youtube
    : draft.channelData?.youtube

  return {
    youtube: {
      ...(data?.title ? { title: data.title } : {}),
      ...(data?.category?.id ? { categoryId: data.category.id } : {}),
      ...(data?.privacyStatus != null ? { privacy: data.privacyStatus } : {}),
      ...(data?.license != null ? { license: data.license } : {}),
      ...(data?.notifySubscribers != null
        ? { notifySubscribers: data.notifySubscribers }
        : {}),
      ...(data?.embeddable != null ? { embeddable: data.embeddable } : {}),
      ...(data?.madeForKids != null ? { madeForKids: data.madeForKids } : {}),
      ...(data?.is_ai_generated != null
        ? { isAiGenerated: data.is_ai_generated }
        : {}),
    },
  }
}

export const youtube: ServiceMapperEntry = {
  mapper: mapYoutube,
}
